import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  Shield,
  DollarSign,
  Package,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import QuoteForm from "../components/QuoteForm";
import bdc_car from "../img/bdc_car.jpeg";
import bdc_unpacking from "../img/bdc_unpacking.jpeg";

const Home = () => {
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState("left");
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const trustBadges = [
    { icon: Shield, text: "Licensed & Insured" },
    { icon: DollarSign, text: "Transparent Pricing" },
    { icon: Package, text: "Careful Handling" },
  ];

  const steps = [
    {
      number: "1",
      title: "Request a quote",
      description: "Fill out our simple form or give us a call",
      image:
        "https://images.unsplash.com/photo-1624137527136-66e631bdaa0e?w=400&q=80",
    },
    {
      number: "2",
      title: "We confirm details",
      description: "We review your needs and provide a clear quote",
      image:
        "https://images.unsplash.com/photo-1586781383963-8e66f88077ec?w=400&q=80",
    },
    {
      number: "3",
      title: "We move you safely",
      description: "Our professional team handles everything",
      image:
        "https://images.pexels.com/photos/9115461/pexels-photo-9115461.jpeg?auto=compress&cs=tinysrgb&w=400",
    },
  ];

  const services = [
    {
      title: "Local Moving",
      description: "Fast, careful moves across your city.",
      link: "/services",
      image: bdc_car,
    },
    {
      title: "Packing & Unpacking",
      description: "We pack securely to protect your items.",
      link: "/services",
      image: bdc_unpacking,
    },
    {
      title: "Delivery / Last-mile",
      description: "Furniture and business deliveries on time.",
      link: "/services",
      image:
        "https://images.unsplash.com/photo-1624137527136-66e631bdaa0e?w=800&q=80",
    },
  ];

  const reviews = [
    {
      name: "Sarah Johnson",
      rating: 5,
      text: "Fast, careful, and professional. They made our move stress-free!",
      location: "Vancouver",
    },
    {
      name: "Michael Chen",
      rating: 5,
      text: "Clear pricing and great communication throughout the entire process.",
      location: "Surrey",
    },
    {
      name: "Emily Rodriguez",
      rating: 5,
      text: "On time and handled everything safely. Highly recommend!",
      location: "Burnaby",
    },
    {
      name: "David Thompson",
      rating: 5,
      text: "Professional team that went above and beyond. Best movers in BC!",
      location: "Richmond",
    },
  ];

  const trustedCompanies = [
    "CDS",
    "BestBuy",
    "Costco",
    "Samsung",
    "LG",
    "Dockers",
    "Trail appliances",
  ];

  // Auto-rotate reviews every 5 seconds
  useEffect(() => {
    const handleAutoNext = () => {
      setSlideDirection("left");
      if (isMobile) {
        setCurrentReviewIndex((prev) => (prev + 1) % reviews.length);
      } else {
        setCurrentReviewIndex((prev) => (prev + 2) % reviews.length);
      }
    };

    const interval = setInterval(handleAutoNext, 5000);
    return () => clearInterval(interval);
  }, [reviews.length, isMobile]);

  // Auto-rotate services on mobile every 4 seconds
  useEffect(() => {
    if (!isMobile) return;

    const interval = setInterval(() => {
      setCurrentServiceIndex((prev) => (prev + 1) % services.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isMobile, services.length]);

  const handleNext = () => {
    setSlideDirection("left");
    if (isMobile) {
      setCurrentReviewIndex((prev) => (prev + 1) % reviews.length);
    } else {
      setCurrentReviewIndex((prev) => (prev + 2) % reviews.length);
    }
  };

  const handlePrev = () => {
    setSlideDirection("right");
    if (isMobile) {
      setCurrentReviewIndex(
        (prev) => (prev - 1 + reviews.length) % reviews.length,
      );
    } else {
      setCurrentReviewIndex(
        (prev) => (prev - 2 + reviews.length) % reviews.length,
      );
    }
  };

  const handleServiceNext = () => {
    setCurrentServiceIndex((prev) => (prev + 1) % services.length);
  };

  const handleServicePrev = () => {
    setCurrentServiceIndex(
      (prev) => (prev - 1 + services.length) % services.length,
    );
  };

  const currentReviews = isMobile
    ? [reviews[currentReviewIndex]]
    : [
        reviews[currentReviewIndex],
        reviews[(currentReviewIndex + 1) % reviews.length],
      ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-20 bg-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1600518464441-9154a4dea21b?w=1600&q=80"
            alt="Professional moving service"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/90 to-gray-900/70"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="max-w-2xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 animate-fadeInUp">
              Moving made simple. Stress-free and on time.
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 animate-fadeInUp animation-delay-100">
              Local & long-distance moving across BC. Professional crew. Clear
              pricing.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-12 animate-fadeInUp animation-delay-200">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-all duration-300 hover:scale-105"
              >
                Get a Quote
              </Link>
              <a
                href="tel:7783260735"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-full hover:bg-gray-100 transition-all duration-300"
              >
                <Phone size={20} className="mr-2" />
                Call (778) 326-0735
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-6 animate-fadeInUp animation-delay-300">
              {trustBadges.map((badge, index) => (
                <div key={index} className="flex items-center text-sm">
                  <badge.icon size={20} className="mr-2 text-orange-500" />
                  <span>{badge.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Quote Section */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-block mb-4">
              <span className="bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold">
                FREE QUOTE
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Get Your Free Quote
            </h2>
            <p className="text-lg text-gray-600">
              Tell us about your move and we'll get back to you quickly
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-200">
            <QuoteForm />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block mb-4">
              <span className="bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold">
                3 SIMPLE STEPS
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600">Simple and straightforward</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-500 text-white text-3xl font-bold rounded-full mb-6 shadow-lg">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fadeInUp">
            <div className="inline-block mb-4">
              <span className="bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold">
                WHAT WE DO
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-lg text-gray-600">
              Professional moving solutions for every need
            </p>
          </div>

          {/* Desktop: Grid View */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 animate-fadeInUp group border border-gray-200"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <Link
                    to={service.link}
                    className="inline-flex items-center text-orange-500 font-medium hover:text-orange-600 transition-colors"
                  >
                    Learn more
                    <ArrowRight size={16} className="ml-1" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile: Carousel View */}
          <div className="md:hidden relative">
            <div className="overflow-hidden">
              <div key={currentServiceIndex} className="slide-in-left">
                <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 mx-4 border border-gray-200">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={services[currentServiceIndex].image}
                      alt={services[currentServiceIndex].title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {services[currentServiceIndex].title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {services[currentServiceIndex].description}
                    </p>
                    <Link
                      to={services[currentServiceIndex].link}
                      className="inline-flex items-center text-orange-500 font-medium hover:text-orange-600 transition-colors"
                    >
                      Learn more
                      <ArrowRight size={16} className="ml-1" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={handleServicePrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-white hover:bg-orange-500 text-gray-900 hover:text-white p-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110 z-10"
              aria-label="Previous service"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={handleServiceNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-white hover:bg-orange-500 text-gray-900 hover:text-white p-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110 z-10"
              aria-label="Next service"
            >
              <ChevronRight size={20} />
            </button>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-6">
              {services.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentServiceIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    currentServiceIndex === index
                      ? "bg-orange-500 w-6"
                      : "bg-gray-300"
                  }`}
                  aria-label={`Go to service ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews - Slideshow */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fadeInUp">
            <div className="inline-block mb-4">
              <span className="bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold">
                CUSTOMER REVIEWS
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg text-gray-600">
              Real reviews from real customers
            </p>
          </div>

          {/* Carousel Container */}
          <div className="relative max-w-5xl mx-auto">
            <div className="overflow-hidden">
              <div
                key={currentReviewIndex}
                className={`grid grid-cols-1 ${isMobile ? "" : "md:grid-cols-2"} gap-6 ${
                  slideDirection === "left" ? "slide-in-left" : "slide-in-right"
                }`}
              >
                {currentReviews.map((review, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg p-8 shadow-lg border-2 border-gray-100 hover:border-orange-200 hover:shadow-xl transition-all duration-300"
                  >
                    {/* Star Rating */}
                    <div className="flex gap-1 mb-4">
                      {[...Array(review.rating)].map((_, i) => (
                        <svg
                          key={i}
                          className="w-6 h-6 fill-orange-500"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                        </svg>
                      ))}
                    </div>
                    <p className="text-gray-700 text-lg mb-6 italic leading-relaxed">
                      "{review.text}"
                    </p>
                    <div className="border-t border-gray-200 pt-4">
                      <p className="font-bold text-gray-900 text-lg">
                        {review.name}
                      </p>
                      <p className="text-sm text-gray-500">{review.location}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={handlePrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-4 bg-white hover:bg-orange-500 text-gray-900 hover:text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 z-10"
              aria-label="Previous reviews"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-4 bg-white hover:bg-orange-500 text-gray-900 hover:text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 z-10"
              aria-label="Next reviews"
            >
              <ChevronRight size={24} />
            </button>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-8">
              {isMobile
                ? // Mobile: 4 dots (one for each review)
                  reviews.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setSlideDirection(
                          index > currentReviewIndex ? "left" : "right",
                        );
                        setCurrentReviewIndex(index);
                      }}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        currentReviewIndex === index
                          ? "bg-orange-500 w-8"
                          : "bg-gray-300 hover:bg-gray-400"
                      }`}
                      aria-label={`Go to review ${index + 1}`}
                    />
                  ))
                : // Desktop: 2 dots (one for each pair)
                  [0, 2].map((index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setSlideDirection(
                          index > currentReviewIndex ? "left" : "right",
                        );
                        setCurrentReviewIndex(index);
                      }}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        currentReviewIndex === index
                          ? "bg-orange-500 w-8"
                          : "bg-gray-300 hover:bg-gray-400"
                      }`}
                      aria-label={`Go to slide ${index / 2 + 1}`}
                    />
                  ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trusted Companies */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 animate-fadeInUp">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Trusted by Leading Companies
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              Delivering excellence for BC's top retailers
            </p>
          </div>
          <div className="relative overflow-hidden py-6 sm:py-8">
            {/* Gradient Overlays for smooth fade effect */}
            <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-r from-white to-transparent z-10"></div>
            <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-l from-white to-transparent z-10"></div>

            <div className="flex gap-4 sm:gap-8 marquee">
              {[
                ...trustedCompanies,
                ...trustedCompanies,
                ...trustedCompanies,
                ...trustedCompanies,
              ].map((company, index) => (
                <div
                  key={index}
                  className="flex items-center justify-center min-w-[180px] sm:min-w-[240px] h-24 sm:h-32 bg-white rounded-xl shadow-md hover:shadow-xl border-2 border-gray-100 hover:border-orange-200 px-6 sm:px-10 transition-all duration-5 hover:scale-105 group"
                >
                  <div className="text-center">
                    <span className="text-lg sm:text-2xl font-bold text-gray-700 group-hover:text-orange-500 transition-colors duration-5">
                      {company}
                    </span>
                    <div className="h-0.5 bg-gradient-to-r from-transparent via-orange-400 to-transparent mt-2 sm:mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-5"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-center mt-6 sm:mt-8">
            <p className="text-xs sm:text-sm text-gray-500 bg-gray-100 inline-block px-4 sm:px-6 py-2 rounded-full">
              * Representative delivery partners
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-orange-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to move?
          </h2>
          <p className="text-xl mb-8">
            Get a free quote today and experience stress-free moving
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-orange-500 font-semibold rounded-full hover:bg-gray-100 transition-all duration-300 hover:scale-105"
            >
              Get a Quote
            </Link>
            <a
              href="tel:7783260735"
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-orange-500 transition-all duration-300"
            >
              <Phone size={20} className="mr-2" />
              Call Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
